import React, { useEffect, useState } from "react";
import { redirect, useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography, Paper } from "@mui/material";
import axios from "axios";
import { WEBSERVICE, showToast, encryptMessages, handleDefaultAssetPair } from "../../CoreDataService";
import { useChat } from "../../Services/ChatService";

export default function AutoLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(
    "Please wait, we are automatically logging you into your exchange..."
  );
  const { makeUserOnline } = useChat();

  // Log all URL parameters immediately

  console.log("demo");

  useEffect(() => {
    handleAutoLogin();
  }, []);

  // Separate function to get user details
  const getUserDetails = async (uuid, token) => {
    try {
      const userObj = {
        uuid: uuid,
      };

      const response = await axios.post(
        `${WEBSERVICE}user/GetUserDetails`,
        JSON.stringify(userObj),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching user details:", error);
      throw error;
    }
  };

  const handleAutoLogin = async () => {
    try {
      // Extract params from URL
      const brokerId = searchParams.get("brokerId");
      const uuid = searchParams.get("uuid");
      const email = searchParams.get("email");
      const token = searchParams.get("token");

      // Validate required parameters
      if (!brokerId || !uuid || !email || !token) {
        setError("Missing required parameters");
        showToast("error", "Missing required parameters for auto-login");
        return;
      }

      // Set initial data
      localStorage.setItem("BROKERID", brokerId);
      localStorage.setItem("BROKERID_Domain", brokerId);
      localStorage.setItem("uuid", uuid);
      localStorage.setItem("email", decodeURIComponent(email));
      localStorage.setItem("token", token);

      const exchangeTokenExpiry = new Date();
      exchangeTokenExpiry.setMonth(exchangeTokenExpiry.getMonth() + 12);
      document.cookie = `exchange_token=${token};expires=${exchangeTokenExpiry};domain=.paybito.com;path=/`;

      setLoadingMessage("Verifying credentials...");

      // UNCOMMENT THIS SECTION - Call the separate getUserDetails function

      const response = await axios.post(
        WEBSERVICE + "/user/GetUserAppSettings",
        { uuid: uuid },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${token}`,
          },
        }
      );


      const result = response.data;

      localStorage.setItem(
        "twoFactorAuth",
        result.userAppSettingsResult.two_factor_auth
      );
      const data = await getUserDetails(uuid, token);

      if (data.error && data.error.error_data === 0) {
        setLoadingMessage("Login successful! Redirecting...");
        await processLoginData(data, token);
      } else {
        setError(data.error?.error_msg || "Auto-login failed");
        showToast("error", data.error?.error_msg || "Auto-login failed");
      }
    } catch (error) {
      console.error("Auto-login error:", error);
      const errorMessage =
        error.response?.data?.error?.error_msg ||
        "Failed to auto-login. Please check your credentials.";
      setError(errorMessage);
      showToast("error", errorMessage);
    }
  };

  const processLoginData = async (data, accessToken) => {
    try {
      const userDetails = data.userResult;
      const uuid = userDetails.uuid;

      // Set tokens and session data - THIS IS CRITICAL for authentication
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("token", accessToken);

      const exchangeTokenExpiry = new Date();
      exchangeTokenExpiry.setMonth(exchangeTokenExpiry.getMonth() + 12);
      document.cookie = `exchange_token=${accessToken};expires=${exchangeTokenExpiry};domain=.paybito.com;path=/`;
      localStorage.setItem("uuid", uuid);
      localStorage.setItem("userId", userDetails.userId);

      // Set cookies with 12-month expiration
      const myDate = new Date();
      myDate.setMonth(myDate.getMonth() + 12);

      document.cookie = `Username=${userDetails.firstName};expires=${myDate};domain=.paybito.com;path=/`;
      document.cookie = `userId=${userDetails.userId};expires=${myDate};domain=.paybito.com;path=/`;
      document.cookie = `ssecca=${accessToken};expires=${myDate};domain=.paybito.com;path=/`;

      // Set user profile data
      localStorage.setItem("user_name", userDetails.firstName);
      localStorage.setItem("first_name", userDetails.firstName);
      localStorage.setItem("last_name", userDetails.lastName);
      localStorage.setItem("middle_name", userDetails.middleName);
      localStorage.setItem("user_id", userDetails.userId);
      localStorage.setItem("email", userDetails.email);
      localStorage.setItem("profile_pic", userDetails.profilePic);
      localStorage.setItem("userCountry", userDetails.country);

      // Set verification dates
      localStorage.setItem(
        "tier2SubmissionDate",
        userDetails.tier2SubmitDate || ""
      );
      localStorage.setItem(
        "tier3SubmissionDate",
        userDetails.tier3SubmitDate || ""
      );
      localStorage.setItem(
        "tier2ApprovedDate",
        userDetails.tier2ApprovedDate || ""
      );
      localStorage.setItem(
        "tier3ApprovedDate",
        userDetails.tier3ApprovedDate || ""
      );

      // Set trading preferences
      localStorage.setItem("check_id_verification_status", "true");
      const pair = data?.defaultAssetPair || data?.userResult?.defaultAssetPair;
      if (pair) {
        handleDefaultAssetPair(pair, true);
      } else {
        localStorage.setItem("selected_currency", "btc");
        localStorage.setItem("buying_crypto_asset", "btc");
        localStorage.setItem("O_buying_crypto_asset", "btc");
        localStorage.setItem("selling_crypto_asset", "usdt");
        localStorage.setItem("O_selling_crypto_asset", "usdt");
      }
      localStorage.setItem("UserTiretype", userDetails.userTierType);
      localStorage.setItem("exist2FaKey", userDetails.exist2FaKey);

      // Update brokerId if different
      if (localStorage.getItem("BROKERID") !== userDetails.brokerId) {
        localStorage.setItem("BROKERID", userDetails.brokerId);
      }

      // Make user online in chat
      makeUserOnline({
        userId: encryptMessages(userDetails.userId),
      });

      // Register FCM token if available
      if (localStorage.getItem("fcmToken")) {
        await callFcmTokenRegister(accessToken, uuid);
      }

      // Route to appropriate location
      await routeToLocation(userDetails);
    } catch (error) {
      console.error("Error processing login data:", error);
      setError("Error processing login. Please try again later.");
      showToast("error", "Failed to process login data");
    }
  };

  const routeToLocation = async (userDetails) => {
    const path = searchParams.get("path");
    if (path) {
      navigate(`/${path}`);
      return;
    }

    const callFrom = searchParams.get("callFrom");
    if (callFrom === "payments") {
      const invoicePayData = {
        invoice_id: searchParams.get("invoice_id") || "",
        merchant_id: searchParams.get("merchant_id") || "",
        currency: searchParams.get("currency") || "",
        fiat_amount: searchParams.get("fiat_amount") || "",
        description: searchParams.get("description") || "",
        amount_to_be_paid: searchParams.get("amount_to_be_paid") || "",
        currency_id: searchParams.get("currency_id") || "",
        transaction_address: searchParams.get("transaction_address") || "",
        network: searchParams.get("network") || "",
      };
      localStorage.setItem("invoicePayData", JSON.stringify(invoicePayData));
      const params = new URLSearchParams(invoicePayData).toString();
      navigate(`/my-wallet`);
    } else {
      try {
        const currentDate = new Date();
        const tier2ApprovedDate = new Date(userDetails.tier2ApprovedDate || "");
        const tier3ApprovedDate = new Date(userDetails.tier3ApprovedDate || "");

        const diffDays =
          tier2ApprovedDate &&
          Math.ceil(
            Math.abs(currentDate - tier2ApprovedDate) / (1000 * 60 * 60 * 24)
          );

        const diffDayst3 =
          tier3ApprovedDate &&
          Math.ceil(
            Math.abs(currentDate - tier3ApprovedDate) / (1000 * 60 * 60 * 24)
          );

        // Check if user tier is 2 or 3
        if (userDetails?.userTierType == 2 || userDetails?.userTierType == 3) {
          navigateBasedOnPermission(userDetails);
        }
        // Check if tier 2 re-KYC is required
        else if (
          (new Date(userDetails.created) <
            new Date(userDetails?.reKycCheckDate) &&
            !userDetails.tier2SubmitDate) ||
          (tier2ApprovedDate && diffDays >= 365)
        ) {
          navigate("/identity-verification");
        }
        // Check if tier 3 re-KYC is required
        else if (
          userDetails.userDocsStatus === "1" &&
          ((new Date(userDetails.created) <
            new Date(userDetails.reKycCheckDate) &&
            !userDetails.tier3SubmitDate) ||
            diffDayst3 >= 365)
        ) {
          navigate("/identity-verification");
        }
        // Default routing
        else {
          navigateBasedOnPermission(userDetails);
        }
      } catch (error) {
        console.error("Error in routing:", error);
        // Default fallback navigation
        navigate("/");
      }
    }
  };

  const navigateBasedOnPermission = (userDetails) => {
    const { isSpot, isFutures, isOptions } = userDetails;

    if (isSpot === 0) {
      if (isFutures === 0) {
        if (isOptions === 0) {
          navigate("/profile-details");
        } else {
          navigate("/options");
        }
      } else {
        navigate("/futures");
      }
    } else {
      navigate("/spot");
    }
  };

  const callFcmTokenRegister = async (token, uuid) => {
    try {
      const userObj = {
        platform: "WEB",
        uuid: uuid,
        token: localStorage.getItem("fcmToken"),
        audience: "TRADER",
      };

      await axios.post(
        `${WEBSERVICE}notification/updateFCMToken`,
        JSON.stringify(userObj),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + token,
          },
        }
      );

      console.log("FCM token registered successfully");
    } catch (error) {
      console.error("Error registering FCM token:", error);
      // Non-critical error, don't block the login flow
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        px: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 6,
          borderRadius: 4,
          backgroundColor: "#ffffff",
          textAlign: "center",
          maxWidth: 500,
          width: "100%",
        }}
      >
        {!error ? (
          <>
            <CircularProgress
              size={80}
              thickness={4}
              sx={{
                mb: 4,
                color: "#667eea",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: "#333",
                fontWeight: 600,
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              {loadingMessage}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                mt: 2,
              }}
            >
              You will be redirected shortly...
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                color: "error.main",
                fontWeight: 600,
                mb: 2,
              }}
            >
              {error}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
              }}
            >
              Please contact support if the issue persists.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
}
